import React, { useState, useRef, useEffect } from 'react'
import { Group, MultiSelect, MultiSelectProps } from '@mantine/core'
import { usePreNoticeListQuery } from '../../_queries/usePreNoticeListQuery'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { Controller, useFormContext } from 'react-hook-form'
import { IApplyPreNotice } from 'app/pre-notice/_types'
import { renderToast } from '@/components/Toast'
import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
const PreNoticeForm = ({
  children,
  onSubmit,
  defaultValues,
}: {
  children: React.ReactNode
  onSubmit: (data: IApplyPreNotice) => void
  defaultValues?: string
}) => {
  const { data } = usePreNoticeListQuery()
  const multiSelectRef = useRef<HTMLInputElement>(null)

  const {
    setValue,
    control,
    getValues,
    register,
    handleSubmit,
    formState: { errors },
  } = useFormContext<IApplyPreNotice>()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [maxPillWidth, setMaxPillWidth] = useState(180)

  const renderOption: MultiSelectProps['renderOption'] = props => {
    const isSelected = getValues('courseIds').includes(props.option.value)
    return (
      <Group justify="apart" wrap="nowrap" gap="xs">
        <Group gap="xs">
          <div className="flex items-center gap-2">
            <img
              src={isSelected ? '/svg/icon_check_orange.svg' : '/svg/icon_check_gray.svg'}
              alt="checkbox"
              width={20}
              height={20}
            />
            <p>{props.option.label}</p>
          </div>
        </Group>
      </Group>
    )
  }

  const handleChange = (values: string[]) => {
    if (values.length >= 2) {
      setIsDropdownOpen(false)
    }
  }

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev)
  }
  useEffect(() => {
    if (defaultValues && getValues('courseIds').length === 0) {
      setValue('courseIds', [defaultValues])
    }
  }, [defaultValues, getValues, setValue])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-4 px-4 py-2.5">
        <div className="flex flex-col gap-2">
          <label
            htmlFor="name"
            className={`text-sm font-medium lg:text-base lg:font-semibold ${
              errors.courseIds && 'text-red-500'
            }`}
          >
            부트캠프 선택 (최대 2개) <span className="text-red-500">*</span>
          </label>
          <Controller
            name="courseIds"
            control={control}
            rules={{ required: '부트캠프를 최소 1개 이상 선택해주세요' }}
            render={({ field }) => (
              <MultiSelect
                {...field}
                ref={multiSelectRef}
                data={
                  data?.map(item => ({
                    value: item.id,
                    label: item.title,
                  })) || []
                }
                dropdownOpened={isDropdownOpen}
                onDropdownOpen={() => setIsDropdownOpen(true)}
                onDropdownClose={() => setIsDropdownOpen(false)}
                onChange={values => {
                  if (values.length > 2) {
                    renderToast({
                      title: '사전알림은 최대 2개까지 신청 가능해요.',
                      icon: <ExclamationCircleIcon className="h-5 w-5 text-red-500" />,
                    })
                    const limitedValues = values.slice(0, 2)
                    field.onChange(limitedValues)
                    handleChange(limitedValues)
                  } else {
                    field.onChange(values)
                    handleChange(values)
                  }
                }}
                onClick={e => {
                  e.stopPropagation()
                  if (e.target === e.currentTarget || e.target === multiSelectRef.current) {
                    toggleDropdown()
                  }
                }}
                placeholder={field.value.length > 0 ? '' : '부트캠프를 선택해주세요.'}
                rightSection={
                  <div
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      setIsDropdownOpen(!isDropdownOpen)
                    }}
                  >
                    <ChevronDownIcon className="h-4 w-4" />
                  </div>
                }
                searchable={false}
                clearable
                className="cursor-pointer ring-0"
                renderOption={renderOption}
                styles={{
                  input: {
                    border: '1px solid #D4D4D4',
                    borderRadius: '6px',
                    paddingLeft: '12px',
                    cursor: 'pointer',
                    height: '44px',
                    display: 'flex',
                    width: '100%',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    '&:focus': {
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                    },
                  },
                  inputField: {
                    border: 'none',
                    paddingLeft: 0,
                    cursor: 'pointer',
                    width: '100%',
                    '&:focus': {
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                    },
                  },
                  dropdown: {
                    padding: '8px 0',
                    border: 'none',
                    boxShadow: '0 5px 17px 0px rgba(0,0,0,0.12)',
                    borderRadius: '8px',
                  },
                  pillsList: {
                    width: '100%',
                    display: 'flex',
                    gap: '4px',
                    flexWrap: 'nowrap',
                    overflow: 'hidden',
                  },
                  pill: {
                    background: '#F1F1F1',
                    borderRadius: '4px',
                    color: '#737373',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                    flex: '0 1 auto',
                    minWidth: 0,
                    '& > span': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    },
                  },
                  label: {
                    display: 'flex',
                    gap: '4px',
                    width: '100%',
                    flexWrap: 'nowrap',
                    flex: 1,
                    overflow: 'hidden',
                  },
                }}
              />
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label
            htmlFor="phone"
            className={`text-sm font-medium lg:text-base lg:font-semibold ${
              errors.phoneNo && 'text-red-500'
            }`}
          >
            연락처 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('phoneNo', {
              required: '연락처를 입력해주세요.',
              pattern: {
                value: /^\d+$/,
                message: '연락처 형식이 올바르지 않습니다.',
              },
            })}
            onChange={e => {
              // 혹시 다른 방법으로 숫자 아닌 값이 들어왔을 경우 대비
              e.target.value = e.target.value.replace(/[^0-9]/g, '')
              setValue('phoneNo', e.target.value)
            }}
            inputMode="numeric"
            placeholder="연락처를 입력해주세요."
            className="h-[44px] w-full rounded-md border border-[#D4D4D4] p-0 pl-3 text-sm placeholder:text-label-alternative focus:outline-none focus:ring-0 lg:text-base"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label
            htmlFor="email"
            className={`text-sm font-medium lg:text-base lg:font-semibold ${
              errors.email && 'text-red-500'
            }`}
          >
            이메일 <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            {...register('email', {
              required: '이메일을 입력해주세요.',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: '이메일 형식이 올바르지 않습니다.',
              },
            })}
            placeholder="이메일을 입력해주세요."
            className="h-[44px] w-full rounded-md border border-[#D4D4D4] p-0 pl-3 text-sm placeholder:text-label-alternative focus:outline-none focus:ring-0 lg:text-base"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="expect" className="text-sm font-medium lg:text-base lg:font-semibold">
            부트캠프에 기대하는 점
          </label>
          <input
            type="text"
            {...register('reason')}
            placeholder="ex)커리큘럼, 강사님, 프로젝트, 인턴십 등"
            className="h-[44px] w-full rounded-md border border-[#D4D4D4] p-0 pl-3 text-sm placeholder:text-label-alternative focus:outline-none focus:ring-0 lg:text-base"
          />
        </div>
      </div>
      {children}
    </form>
  )
}

export default PreNoticeForm
